import React, { useEffect, useMemo, useRef, useState } from "react";
import LogoMarquee from "./logoMarquee";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { useDispatch, useSelector } from "react-redux";
import { toggleDarkMode } from "../../../redux/slices/partnersSlice";
import { RootState } from "../../../redux/store";

// ---------- Theme types ----------
type Theme = {
  bg: string;
  card: string;
  text: string;
  subtext: string;
  border: string;
  shadow: string;
  accentSolid: string;
  accentGradient: string;
  softBg: string; // for empty coaching placeholder
};

// ---------- Section key typing ----------
type SectionKey =
  | "hero"
  | "features"
  | "founders"
  | "pricing"
  | "advantage"
  | "faq";

// ---------- Helper components prop types ----------
type KPIProps = { label: string; value: string; THEME: Theme };
type MiniStatProps = { label: string; value: string };
type BadgeProps = { THEME: Theme; text: string };
type AdvCardProps = { THEME: Theme; title: string; desc: string };
type FooterLink = { label: string; href: string };
type FooterColProps = { THEME: Theme; title: string; links: FooterLink[] };
type TestimonialProps = {
  THEME: Theme;
  quote: string;
  author: string;
  role: string;
};

function TestimonialCard({ THEME, quote, author, role }: TestimonialProps) {
  return (
    <div style={styles.testimonialCard(THEME)}>
      <FormatQuoteIcon />
      <p style={styles.testimonialQuote(THEME)}>“{quote}”</p>
      <div style={styles.testimonialAuthor(THEME)}>{author}</div>
      <div style={styles.testimonialMeta(THEME)}>{role}</div>
    </div>
  );
}

// ---------- Dark/Light detection ----------
export function usePrefersDarkMode() {
  const get = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDark, setIsDark] = useState<boolean>(get() || false);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    // Safari < 14 fallback
    // @ts-ignore
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      // @ts-ignore
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  return isDark;
}

// ---------- Component ----------
export default function HomeV3() {
  const ACCENT_GRADIENT = "linear-gradient(180deg, #2aa880, #127351)";
  const ACCENT_SOLID = "#2aa880";
  const dispatch = useDispatch();

  const isDark = usePrefersDarkMode();

  const darkmode = useSelector((state: RootState) => state.partners.darkMode);

  useEffect(() => {
    if (darkmode !== isDark) {
      dispatch(toggleDarkMode());
    }
  }, [darkmode, isDark]);

  const LIGHT: Theme = {
    bg: "#f7faf9",
    card: "#ffffff",
    text: "#0b261e",
    subtext: "#425a52",
    border: "#e6eeeb",
    shadow: "0 10px 25px rgba(17, 94, 72, 0.08)",
    accentSolid: ACCENT_SOLID,
    accentGradient: ACCENT_GRADIENT,
    softBg: "#eef6f2",
  };

  const DARK: Theme = {
    bg: "#0b1210",
    card: "#0f1916",
    text: "#e8f2ef",
    subtext: "#a7c1b8",
    border: "#173a30",
    shadow: "0 10px 25px rgba(0,0,0,0.35)",
    accentSolid: ACCENT_SOLID,
    accentGradient: ACCENT_GRADIENT,
    softBg: "#0d1714",
  };

  const THEME: Theme = isDark ? DARK : LIGHT;

  // Responsive breakpoint
  const width = useWindowWidth();
  const isMobile = width < 720;

  // Refs
  const heroRef = useRef<HTMLElement>(null!);
  const featuresRef = useRef<HTMLElement>(null!);
  const foundersRef = useRef<HTMLElement>(null!);
  const pricingRef = useRef<HTMLElement>(null!);
  const advantageRef = useRef<HTMLElement>(null!);
  const faqRef = useRef<HTMLElement>(null!);

  // Visibility flags
  const [visible, setVisible] = useState<Record<SectionKey, boolean>>({
    hero: false,
    features: false,
    founders: false,
    pricing: false,
    advantage: false,
    faq: false,
  });

  // Track if FEATURES section is currently in the viewport (for mobile auto-centering behavior)
  const [featuresInView, setFeaturesInView] = useState<boolean>(true);

  // IntersectionObservers
  useEffect(() => {
    const map: Record<SectionKey, React.RefObject<HTMLElement>> = {
      hero: heroRef,
      features: featuresRef,
      founders: foundersRef,
      pricing: pricingRef,
      advantage: advantageRef,
      faq: faqRef,
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const match = (
            Object.entries(map) as [SectionKey, React.RefObject<HTMLElement>][]
          ).find(([, ref]) => ref.current === entry.target);
          const key = match?.[0];
          if (key && entry.isIntersecting) {
            setVisible((s) => ({ ...s, [key]: true }));
          }
        });
      },
      { threshold: 0.22 }
    );

    (Object.values(map) as React.RefObject<HTMLElement>[]).forEach((r) => {
      if (r.current) io.observe(r.current);
    });

    return () => io.disconnect();
  }, []);

  // Dedicated observer for the FEATURES section (mobile auto-scroll guard)
  useEffect(() => {
    if (!featuresRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => setFeaturesInView(entries[0].isIntersecting),
      { threshold: 0 }
    );
    obs.observe(featuresRef.current);
    return () => obs.disconnect();
  }, []);

  const heroImages = [
    "/images/hero/hero1.png",
    "/images/hero/hero2.png",
    "/images/hero/hero3.png",
    "/images/hero/hero4.png",
    "/images/hero/hero5.png",
    "/images/hero/hero6.png",
    "/images/hero/hero7.png",
  ];

  const [currentHero, setCurrentHero] = useState(0);

  // Preload hero images
  useEffect(() => {
    heroImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Rotate hero background
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Rotating hero subline
  const slogans = useMemo(
    () => [
      "Build habit loops.",
      "Coach better.",
      "Grow faster.",
      "Close more clients.",
      "Retain longer.",
      "Streamline your workflow.",
      "Simplify the process",
    ],
    []
  );
  const [sloganIdx, setSloganIdx] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const DURATION = 5000;
    const FADE_MS = 100;
    let fadeTimeout: number | undefined;
    const interval = window.setInterval(() => {
      setFading(true);
      fadeTimeout = window.setTimeout(() => {
        setSloganIdx((i) => (i + 1) % slogans.length);
        setFading(false);
      }, FADE_MS);
    }, DURATION);
    return () => {
      window.clearInterval(interval);
      if (fadeTimeout) window.clearTimeout(fadeTimeout);
    };
  }, [slogans.length]);

  // 3 features (now include "kind" for sizing caps)
  type FeatureKind = "phone" | "desktop";
  const features = useMemo(
    () => [
      {
        id: "diet",
        title: "Diet",
        bullets: [
          "Track diet with photo AI + notes",
          "Daily macros at a glance",
          "Calories burned & intake trends",
        ],
        icon: "🥗",
        kind: "phone" as FeatureKind,
        image: isDark
          ? "/images/features/diet-dark.gif"
          : "/images/features/diet-light.gif",
      },
      {
        id: "workout",
        title: "Workout",
        bullets: [
          "Create reusable templates",
          "Track active workouts live",
          "Progressive overload insights",
        ],
        icon: "🏋️‍♀️",
        kind: "phone" as FeatureKind,
        image: isDark
          ? "/images/features/workout-dark.gif"
          : "/images/features/workout-light.gif",
      },
      {
        id: "coaching",
        title: "Coaching Platform",
        bullets: [
          "All clients’ data in one place",
          "Diet + training in a single view",
          "Fewer tabs, faster decisions",
        ],
        icon: "📊",
        kind: "desktop" as FeatureKind,
        image: isDark
          ? "/images/features/coaching-platform-dark.gif"
          : "/images/features/coaching-platform-light.gif",
      },
    ],
    [isDark]
  );

  const [activeFeature, setActiveFeature] = useState<number>(0);

  // ----- Mobile: keep horizontal scroll, but DO NOT change highlight on scroll.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Timer bar refs
  const timerFillRefs = useRef<Array<HTMLDivElement | null>>([]);

  const restartTimerAnimation = (activeIdx: number) => {
    timerFillRefs.current.forEach((el, idx) => {
      if (!el) return;
      el.style.transition = "none";
      el.style.width = "0%";
    });
    const activeEl = timerFillRefs.current[activeIdx];
    if (activeEl) {
      // force reflow
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      activeEl.offsetWidth;
      activeEl.style.transition = "width 5000ms linear";
      activeEl.style.width = "100%";
    }
  };

  useEffect(() => {
    restartTimerAnimation(activeFeature);
  }, [activeFeature, isMobile]);

  useEffect(() => {
    if (!isMobile || !scrollRef.current) return;
    if (!featuresInView) return;
    const node = cardRefs.current[activeFeature];
    if (!node) return;
    node.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeFeature, isMobile, featuresInView]);

  const selectFeature = (i: number) => setActiveFeature(i);

  // Preload feature images
  useEffect(() => {
    features.forEach((f) => {
      if (!f.image) return;
      const img = new Image();
      img.src = f.image;
    });
  }, [features]);

  // --- Media sizing: make the container exactly the size of the GIF ---
  const featuresColumnRef = useRef<HTMLDivElement | null>(null);
  const mediaPaneRef = useRef<HTMLDivElement | null>(null);

  const [mediaDims, setMediaDims] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  const [imgLoadedKey, setImgLoadedKey] = useState<number>(0); // to retrigger fade

  // Helper to compute desired width caps by kind + layout
  const getMaxWidthFor = (kind: FeatureKind) => {
    if (isMobile) {
      // leave a little horizontal padding on mobile
      return Math.min(
        Math.max(window.innerWidth - 32, 280),
        kind === "phone" ? 420 : 560
      );
    }
    // desktop pane: allow wider coaching GIF, keep phone-like compact
    return Math.min(
      mediaPaneRef.current?.clientWidth || 960,
      kind === "phone" ? 420 : 960
    );
  };

  // Load active image, measure natural size, compute rendered dims
  useEffect(() => {
    const f = features[activeFeature];
    if (!f?.image) {
      setMediaDims({ w: 0, h: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => {
      const naturalW = img.naturalWidth || 1;
      const naturalH = img.naturalHeight || 1;
      const maxW = getMaxWidthFor(f.kind);
      const renderW = Math.min(maxW, naturalW);
      const renderH = Math.round((naturalH / naturalW) * renderW);
      setMediaDims({ w: renderW, h: renderH });
      setImgLoadedKey((k) => k + 1);
    };
    img.src = f.image;
    // Recalculate on resize
    const onResize = () => {
      const naturalW = img.naturalWidth || 0;
      const naturalH = img.naturalHeight || 0;
      if (!naturalW || !naturalH) return;
      const maxW = getMaxWidthFor(f.kind);
      const renderW = Math.min(maxW, naturalW);
      const renderH = Math.round((naturalH / naturalW) * renderW);
      setMediaDims({ w: renderW, h: renderH });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFeature, isMobile, features, isDark]);

  // FAQs
  const faqs = [
    {
      q: "Who is Avro for?",
      a: "Personal trainers, dietitians, and small studios that want a simple, all-in-one system to track client progress across diet and training without juggling multiple tools.",
    },
    {
      q: "Do my clients get the mobile app?",
      a: "Yes. Every seat includes full Pro access to the mobile app—normally $6/month per consumer—bundled at no extra cost.",
    },
    {
      q: "Is there a contract or setup fee?",
      a: "No contracts, no setup fees. Cancel anytime.",
    },
    {
      q: "How many seats are included?",
      a: "150 seats are included at the flat $150/month price.",
    },
    {
      q: "Can I import existing clients?",
      a: "Yes, you can import via CSV and follow a short migration checklist.",
    },
  ];

  return (
    <div style={styles.page(THEME)}>
      {/* HERO */}
      <section
        ref={heroRef}
        style={{
          ...styles.section(),
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background cross-fade stack */}
        <div style={styles.heroBgWrap as React.CSSProperties}>
          {heroImages.map((src, i) => (
            <div
              key={src}
              style={styles.heroBgLayer(src, i === currentHero)}
              aria-hidden
            />
          ))}
          <div style={styles.heroOverlay} aria-hidden />
        </div>

        {/* Foreground content */}
        <div
          style={{
            ...styles.container(isMobile),
            ...revealStyle(visible.hero),
            position: "relative",
            zIndex: 1,
          }}
          aria-label="Hero"
        >
          <div style={styles.heroWrap(isMobile)}>
            <div style={styles.heroLeft(isMobile)}>
              <Badge THEME={THEME} text="New features • AI powered" />
              <h1 style={styles.h1(THEME, isMobile)}>
                Count{" "}
                <span style={{ color: THEME.accentSolid }}>everything.</span>
                <br />
                <span
                  style={styles.rotatorWrap(isMobile)}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span style={styles.rotatorText(THEME, fading)}>
                    {slogans[sloganIdx]}
                  </span>
                </span>
              </h1>
              <p
                style={{ ...styles.lead(THEME, isMobile), color: "lightgray" }}
              >
                1000+ hours of workouts logged <br /> 250+ tons of food scanned
              </p>
              <div
                style={{
                  ...styles.ctaRow(isMobile),
                  justifyContent: "center",
                  padding: "4px 36px 4px 36px",
                }}
              >
                <a
                  href="/onboarding"
                  style={{
                    ...buttonPrimary(THEME),
                    padding: "8px 36px 8px 36px",
                    width: 300,
                  }}
                  aria-label="See pricing"
                >
                  Get started
                </a>
              </div>
              <p
                style={{
                  ...styles.subtle(THEME),
                  color: "lightgray",
                  marginBottom: 60,
                }}
              >
                No contracts. Cancel anytime. 100 seats included.
              </p>
              <LogoMarquee isMobile={isMobile} gap={28} />
            </div>{" "}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        ref={featuresRef}
        style={styles.section(isDark ? THEME.bg : "#fff")}
      >
        <div
          style={{
            ...styles.container(isMobile),
            ...revealStyle(visible.features),
          }}
        >
          <h2 style={styles.h2(THEME, isMobile)}>
            Everything{" "}
            <span style={{ color: THEME.accentSolid }}>you need</span>,<br />{" "}
            nothing you don’t.
          </h2>
          <p style={styles.sectionLead(THEME, isMobile)}>
            Three pillars—diet, training, and coaching—working together.
          </p>

          {/* Desktop: two-pane layout (left cards column, right media) */}
          {!isMobile ? (
            <div style={styles.featuresTwoPane as React.CSSProperties}>
              {/* Left: Column of cards */}
              <div ref={featuresColumnRef} style={styles.featuresColumn}>
                {features.map((f, i) => {
                  const isActiveCard = i === activeFeature;
                  return (
                    <div
                      key={f.id}
                      style={featureCardStyle(THEME, isActiveCard, false)}
                      role="button"
                      aria-label={f.title}
                      tabIndex={0}
                      onClick={() => selectFeature(i)}
                    >
                      {/* Timer bar */}
                      <div style={styles.timerTrack(THEME, isActiveCard)}>
                        <div
                          ref={(el) => {
                            timerFillRefs.current[i] = el;
                          }}
                          style={styles.timerFillInit as React.CSSProperties}
                        />
                      </div>

                      <div style={styles.featureIcon(isActiveCard)} aria-hidden>
                        {f.icon}
                      </div>
                      <h3
                        style={styles.featureTitleEnhanced(THEME, isActiveCard)}
                      >
                        {f.title}
                      </h3>
                      <ul style={styles.featureList as React.CSSProperties}>
                        {f.bullets.map((b: string, idx: number) => (
                          <li
                            key={idx}
                            style={styles.featureItemEnhanced(
                              THEME,
                              isActiveCard
                            )}
                          >
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Right: Media that swaps with active feature */}
              <div
                ref={mediaPaneRef}
                style={styles.mediaPane as React.CSSProperties}
                aria-hidden
              >
                <div style={styles.mediaOuter as React.CSSProperties}>
                  {/* Sized wrapper equals GIF size */}
                  <div
                    style={{
                      ...styles.mediaExactWrap,
                      width: mediaDims.w || undefined,
                      height: mediaDims.h || undefined,
                    }}
                  >
                    {/* Key changes on load to retrigger CSS fade */}
                    <img
                      key={imgLoadedKey}
                      src={features[activeFeature]?.image}
                      alt={features[activeFeature]?.title || "feature"}
                      style={styles.mediaImage}
                      onLoad={() => {
                        // no-op; measured in effect
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile: Media on top, horizontal cards row below */}
              <div
                style={{
                  ...styles.mediaOuter,
                  marginBottom: 12,
                }}
                aria-hidden
              >
                <div
                  style={{
                    ...styles.mediaExactWrap,
                    width: mediaDims.w || undefined,
                    height: mediaDims.h || undefined,
                  }}
                >
                  <img
                    key={imgLoadedKey}
                    src={features[activeFeature]?.image}
                    alt={features[activeFeature]?.title || "feature"}
                    style={styles.mediaImage}
                  />
                </div>
              </div>

              <div
                ref={scrollRef}
                style={styles.featureScrollRow as React.CSSProperties}
              >
                {features.map((f, i) => {
                  const isActiveCard = i === activeFeature;
                  return (
                    <div
                      key={f.id}
                      ref={(el) => {
                        cardRefs.current[i] = el;
                      }}
                      style={featureCardMobileStyle(THEME, isActiveCard)}
                      role="button"
                      aria-label={f.title}
                      tabIndex={0}
                      onClick={() => selectFeature(i)}
                    >
                      {/* Timer bar */}
                      <div style={styles.timerTrack(THEME, isActiveCard)}>
                        <div
                          ref={(el) => {
                            timerFillRefs.current[i] = el;
                          }}
                          style={styles.timerFillInit as React.CSSProperties}
                        />
                      </div>

                      <div style={styles.featureIcon(isActiveCard)} aria-hidden>
                        {f.icon}
                      </div>
                      <h3
                        style={styles.featureTitleEnhanced(THEME, isActiveCard)}
                      >
                        {f.title}
                      </h3>
                      <ul style={styles.featureList as React.CSSProperties}>
                        {f.bullets.map((b: string, idx: number) => (
                          <li
                            key={idx}
                            style={styles.featureItemEnhanced(
                              THEME,
                              isActiveCard
                            )}
                          >
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* FOUNDERS */}
      <section ref={foundersRef} style={styles.section()}>
        <div
          style={{
            ...styles.container(isMobile),
            ...revealStyle(visible.founders),
          }}
          id="founders"
        >
          <div style={styles.foundersCard(THEME, isMobile)}>
            <div style={styles.foundersBadge(THEME)}>
              Word from the founders
            </div>
            <h2 style={styles.h2(THEME, isMobile)}>Why we built Avro</h2>
            <p style={styles.copy(THEME, isMobile)}>
              We were frustrated by “tool sprawl”—spreadsheets for food logs,
              another app for workouts, yet another for chat. Avro compresses
              the workflow into one place so trainers and dietitians can{" "}
              <strong>count everything</strong>, spot trends faster, and deliver
              a premium client experience without premium complexity.
            </p>
            <p style={styles.copy(THEME, isMobile)}>
              Our goal is to make your coaching <em>effortless to scale</em>:
              cleaner data in, clearer decisions out, better results—and more
              word of mouth.
            </p>
            <div style={styles.signatureRow as React.CSSProperties}>
              <div style={styles.sigLeft as React.CSSProperties}>
                <div style={styles.sigLine(THEME)} />
                <div style={styles.sigNames(THEME)}>— The Avro Team</div>
              </div>
              <a
                href="/onboarding"
                style={buttonPrimary(THEME)}
                aria-label="View pricing"
              >
                Get started
              </a>
            </div>
          </div>
          {/* Testimonials (in the same section as Why we built Avro) */}
          <div style={styles.testimonialsRow(isMobile)}>
            <TestimonialCard
              THEME={THEME}
              quote="The client dashboard feels so sleek. I can spot diet gaps and programming plateaus in minutes."
              author="Jenna R."
              role="Studio Owner • Miami"
            />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        ref={pricingRef}
        id="pricing"
        style={styles.section(isDark ? THEME.bg : "#fff")}
      >
        <div
          style={{
            ...styles.container(isMobile),
            ...revealStyle(visible.pricing),
          }}
        >
          <h2 style={styles.h2(THEME, isMobile)}>
            <span style={{ color: THEME.accentSolid }}>Simple </span> - fair
            pricing
          </h2>
          <p style={styles.sectionLead(THEME, isMobile)}>
            No add-ons. No surprises. Cancel anytime.
          </p>

          <div style={styles.pricingCardsRow(isMobile)}>
            {/* Direct to Consumer Pricing */}
            <div style={styles.pricingCard(THEME)}>
              <div style={{ fontSize: 12, fontWeight: 700, color: THEME.subtext, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 8 }}>Individual</div>
              <div style={styles.priceTop(THEME)}>
                <div style={styles.priceValue(THEME)}>$6</div>
                <div style={styles.priceSub(THEME)}>/ month</div>
              </div>
              <ul style={styles.priceList}>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  For <u>individual</u> clients
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Full Pro access to mobile app
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Track workouts and diet
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  AI meal photo analysis
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Cancel anytime
                </li>
              </ul>
              <a
                href="https://apps.apple.com/us/app/avro-workout-diet-tracker/id6739968450"
                style={{ ...buttonPrimary(THEME), display: "block", textAlign: "center" as const, width: "100%", boxSizing: "border-box" as const }}
              >
                Start Pro
              </a>
            </div>

            {/* Coaching Platform Pricing — POPULAR */}
            <div style={{
              ...styles.pricingCard(THEME),
              border: `2px solid ${THEME.accentSolid}`,
              position: "relative" as const,
              boxShadow: `0 20px 40px rgba(42, 168, 128, 0.12), ${THEME.shadow}`,
            }}>
              {/* Popular badge */}
              <div style={{
                position: "absolute" as const,
                top: -12,
                left: "50%",
                transform: "translateX(-50%)",
                background: THEME.accentGradient,
                color: "#fff",
                padding: "4px 16px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase" as const,
                whiteSpace: "nowrap" as const,
              }}>★ MOST POPULAR</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: THEME.accentSolid, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 8, marginTop: 4 }}>Coaching Platform</div>
              <div style={styles.priceTop(THEME)}>
                <div style={styles.priceValue(THEME)}>$150</div>
                <div style={styles.priceSub(THEME)}>/ month</div>
              </div>
              <ul style={styles.priceList}>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  For <u>trainers</u> and <u>dietitians</u>
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Includes 150 Pro seats ($900 value)
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Centralized coaching platform
                </li>
                <li style={styles.priceItem(THEME)}>
                  <span style={{ color: THEME.accentSolid, marginRight: 8, fontWeight: 700 }}>✓</span>
                  Client data: diet + workouts
                </li>
              </ul>
              <a href="/onboarding" style={{ ...buttonPrimary(THEME), display: "block", textAlign: "center" as const, width: "100%", boxSizing: "border-box" as const }}>
                Start Coaching
              </a>
            </div>
          </div>
        </div>
        <p style={{ marginTop: 120, textAlign: "center", fontSize: 16, color: THEME.subtext, maxWidth: 540, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
          No add-ons, no tiers, <u><i>no bs</i></u>. Avro helps you scale your business without scaling your costs.
        </p>
      </section>

      {/* UNFAIR ADVANTAGE - new layout matching provided example */}
      <section ref={advantageRef} style={styles.section()}>
        <div
          style={{
            ...styles.container(isMobile),
            ...revealStyle(visible.advantage),
          }}
        >
          <div style={styles.advWrap(isMobile)}>
            {/* Left image on desktop; on mobile this is ordered second */}
            <div style={styles.advImageCol(isMobile)} aria-hidden>
              <img
                src="/images/grow.jpg"
                alt="Grow faster with Avro"
                style={styles.advImage}
              />
            </div>

            {/* Right copy (first on desktop, first on mobile with image moved below) */}
            <div style={styles.advCopyCol(THEME, isMobile)}>
              <h2 style={styles.h2(THEME, isMobile)}>
                {" "}
                Your <span style={{ color: THEME.accentSolid }}>
                  unfair
                </span>{" "}
                advantage
              </h2>
              <p style={styles.sectionLead(THEME, isMobile)}>
                We help trainers manage and grow their business while providing
                their clients a premium exprience
              </p>

              <div style={styles.advStats as React.CSSProperties}>
                <div style={styles.statBlock(THEME)}>
                  <div style={styles.statHeadline(THEME)}>
                    Better data visibility
                  </div>
                  <div style={styles.statSub(THEME)}>
                    by using our diet & training dashboard, state of the art
                    model and detailed client page.
                  </div>
                </div>

                <div style={styles.statBlock(THEME)}>
                  <div style={styles.statHeadline(THEME)}>
                    2x your client capacity
                  </div>
                  <div style={styles.statSub(THEME)}>
                    when higher-value coaching replaces low-value tasks.
                  </div>
                </div>

                <div style={styles.statBlock(THEME)}>
                  <div style={styles.statHeadline(THEME)}>
                    &gt;50% time saved{" "}
                  </div>
                  <div style={styles.statSub(THEME)}>
                    with our state of the art Coaching Platform.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p style={{ fontSize: 48, marginTop: "20%", textAlign: "center" }}>
            Give your clients the{" "}
            <span style={{ color: THEME.accentSolid }}>tools</span> they need to
            succeed.{" "}
          </p>
          <p style={{ textAlign: "center" }}>
            With coaching tools{" "}
            <u>
              <i>for your</i>
            </u>{" "}
            success.{" "}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        ref={faqRef}
        id="faq"
        style={styles.section(isDark ? THEME.bg : "#fff")}
      >
        <div
          style={{ ...styles.container(isMobile), ...revealStyle(visible.faq) }}
        >
          <h2 style={styles.h2(THEME, isMobile)}>Frequently asked questions</h2>
          <div style={styles.faqWrap as React.CSSProperties}>
            {faqs.map((f, i) => (
              <details
                key={i}
                style={styles.detail(THEME) as React.CSSProperties}
              >
                <summary style={styles.summary(THEME) as React.CSSProperties}>
                  {f.q}
                </summary>
                <p style={styles.faqAnswer(THEME)}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer(THEME)}>
        <div style={styles.footerInner(isMobile)}>
          <div style={styles.footerBrand as React.CSSProperties}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <img
                src="/icon192.png"
                alt="Avro"
                style={{ width: 36, height: 36, borderRadius: 10 }}
              />
              <div style={styles.footerLogo(THEME)} aria-hidden>
                AVRO
              </div>
            </div>
            <p style={styles.footerText(THEME)}>
              Count everything. Coach better. Grow faster.
            </p>
          </div>
          <div style={styles.footerColsWrap(isMobile)}>
            <FooterCol
              THEME={THEME}
              title="Product"
              links={[
                { label: "Features", href: "#features" },
                { label: "Pricing", href: "#pricing" },
                { label: "FAQ", href: "#faq" },
              ]}
            />
            <FooterCol
              THEME={THEME}
              title="Company"
              links={[
                { label: "About Us", href: "/AboutUs" },
                { label: "Founders' Note", href: "#founders" },
                { label: "Contact", href: "mailto:support@avrofitness.com" },
              ]}
            />
            <FooterCol
              THEME={THEME}
              title="Legal"
              links={[
                { label: "Terms of Service", href: "/TOS" },
                { label: "Privacy Policy", href: "/PrivacyPolicy" },
              ]}
            />
          </div>
        </div>
        <div style={styles.footerBottom(THEME)}>
          <span>© {new Date().getFullYear()} Avro Fitness LLC</span>
          <span>Made with ❤️ for coaches & dietitians</span>
        </div>
      </footer>
    </div>
  );
}

/* --------------------------- Hooks & helpers --------------------------- */

function useWindowWidth(): number {
  const [w, setW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

function revealStyle(isVisible: boolean): React.CSSProperties {
  return {
    transition: "opacity 700ms ease, transform 700ms ease",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0px)" : "translateY(12px)",
    willChange: "opacity, transform",
  };
}

function Badge({ THEME, text }: BadgeProps) {
  return (
    <div style={styles.badge(THEME)}>
      <span style={{ fontSize: 12, letterSpacing: 0.3 }}>{text}</span>
    </div>
  );
}

function FooterCol({ THEME, title, links }: FooterColProps) {
  return (
    <div style={styles.footerCol as React.CSSProperties}>
      <div style={styles.footerColTitle(THEME)}>{title}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {links.map((l: FooterLink, i: number) => (
          <li key={i} style={{ marginBottom: 8 }}>
            <a href={l.href} style={styles.footerLink(THEME)}>
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* --------------------------- Styles (inline only) --------------------------- */

const styles: any = {
  testimonialsRow: (isMobile: boolean): React.CSSProperties => ({
    // display: "grid",
    // gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    // gap: 16,
    marginTop: 180,
  }),

  testimonialCard: (THEME: Theme): React.CSSProperties => ({
    justifyContent: "center",
    padding: 16,
  }),

  testimonialQuote: (THEME: Theme): React.CSSProperties => ({
    margin: 0,
    fontSize: 16,
    lineHeight: 1.5,
    color: THEME.text,
  }),

  testimonialAuthor: (THEME: Theme): React.CSSProperties => ({
    marginTop: 12,
    fontWeight: 700,
    color: THEME.text,
  }),

  testimonialMeta: (THEME: Theme): React.CSSProperties => ({
    marginTop: 2,
    fontSize: 13.5,
    color: THEME.subtext,
  }),

  page: (THEME: Theme): React.CSSProperties => ({
    background: THEME.bg,
    color: THEME.text,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    lineHeight: 1.4,
  }),

  section: (bg?: string): React.CSSProperties => ({
    background: bg || "transparent",
    padding: "72px 16px",
    minHeight: "90vh",
  }),

  container: (isMobile: boolean): React.CSSProperties => ({
    maxWidth: 1080,
    margin: "0 auto",
    padding: isMobile ? "0 4px" : "0 8px",
  }),

  // Hero
  heroWrap: (isMobile: boolean): React.CSSProperties => ({
    display: "flex",
    gap: isMobile ? 20 : 28,
    alignItems: "center",
    justifyContent: "center",
  }),

  heroLeft: (isMobile: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    gap: isMobile ? 14 : 18,
    padding: 12,
    borderRadius: 20,
  }),
  h1: (_THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    fontSize: isMobile ? 34 : 72,
    letterSpacing: -0.4,
    margin: 0,
    color: "white",
  }),
  lead: (THEME: Theme, _isMobile: boolean): React.CSSProperties => ({
    fontSize: 16,
    color: THEME.subtext,
    margin: 0,
  }),
  ctaRow: (isMobile: boolean): React.CSSProperties => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: isMobile ? 6 : 10,
  }),
  subtle: (THEME: Theme): React.CSSProperties => ({
    fontSize: 12,
    color: THEME.subtext,
    marginTop: 6,
  }),
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
    marginBottom: 12,
  },
  kpi: (THEME: Theme): React.CSSProperties => ({
    background: "#f1f7f4",
    border: `1px solid ${THEME.border} `,
    borderRadius: 12,
    padding: "10px 12px",
    textAlign: "center",
  }),
  kpiValue: (THEME: Theme): React.CSSProperties => ({
    fontWeight: 800,
    fontSize: 18,
    color: THEME.text,
  }),
  kpiLabel: (THEME: Theme): React.CSSProperties => ({
    fontSize: 12,
    color: THEME.subtext,
  }),
  previewCard: (THEME: Theme): React.CSSProperties => ({
    background: THEME.card,
    border: `1px solid ${THEME.border} `,
    borderRadius: 14,
    padding: 12,
  }),
  previewHeader: (THEME: Theme): React.CSSProperties => ({
    fontWeight: 700,
    marginBottom: 10,
    display: "flex",
    gap: 8,
    alignItems: "center",
  }),
  previewBody: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
  },
  miniStat: {
    background: "#f6faf8",
    borderRadius: 10,
    padding: "10px 12px",
    textAlign: "center",
  },

  // Rotator styles
  rotatorWrap: (isMobile: boolean): React.CSSProperties => ({
    display: "inline-block",
    minWidth: isMobile ? 220 : 420,
  }),
  rotatorText: (_THEME: Theme, fading: boolean): React.CSSProperties => ({
    display: "inline-block",
    transition: "opacity 350ms ease, transform 350ms ease",
    opacity: fading ? 0 : 1,
    transform: fading ? "translateY(6px)" : "translateY(0)",
  }),

  // Hero background cross-fade
  heroBgWrap: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
  },
  heroBgLayer: (src: string, active: boolean): React.CSSProperties => ({
    position: "absolute",
    inset: 0,
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    opacity: active ? 1 : 0,
    transform: active ? "scale(1.0)" : "scale(1.02)",
    transition: "opacity 900ms ease, transform 1600ms ease",
    willChange: "opacity, transform",
  }),
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(0deg, rgba(0,0,0,0.55), rgba(0,0,0,0.55))",
  },

  // Features headings
  h2: (_THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    fontSize: isMobile ? 28 : 34,
    letterSpacing: -0.2,
    margin: 0,
    marginBottom: 6,
    fontWeight: "bold",
  }),
  sectionLead: (THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    marginTop: 4,
    marginBottom: 18,
    color: THEME.subtext,
    fontSize: isMobile ? 15 : 17,
  }),

  // Desktop: two-pane layout
  featuresTwoPane: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 420px) 1fr",
    gap: 18,
    alignItems: "start",
  },

  // Desktop cards column
  featuresColumn: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 14,
    marginTop: 4,
  },

  // Mobile horizontal scroller
  featureScrollRow: {
    display: "flex",
    gap: 12,
    overflowX: "auto",
    padding: "6px 4px 8px 4px",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },

  // Media pane (desktop right side)
  mediaPane: {
    width: "100%",
    position: "relative",
  },

  // New media wrappers to make container == gif size
  mediaOuter: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mediaExactWrap: {
    display: "inline-block",
    overflow: "hidden",
    borderRadius: 17,
    boxShadow: "0 20px 40px rgba(0,0,0,0.20)",
    background: "transparent",
    transition: "width 250ms ease, height 250ms ease",
  } as React.CSSProperties,
  mediaImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: 17,
    imageRendering: "auto",
    // simple fade-in on swap
    opacity: 1,
    transition: "opacity 300ms ease",
  } as React.CSSProperties,

  featureIcon: (active: boolean): React.CSSProperties => ({
    fontSize: 24,
    transform: active ? "scale(1.08)" : "scale(1.0)",
    transition: "transform 250ms ease",
  }),

  // Timer bar
  timerTrack: (THEME: Theme, active: boolean): React.CSSProperties => ({
    position: "relative",
    height: 6,
    width: "100%",
    borderRadius: 999,
    background: active ? "#e7f4ee" : "#eff6f3",
    overflow: "hidden",
    marginBottom: 10,
    border: `1px solid ${active ? "#b6e6d5" : THEME.border} `,
    willChange: "width",
  }),
  timerFillInit: {
    height: "100%",
    width: "0%",
    background: "linear-gradient(180deg, #2aa880, #127351)",
  } as React.CSSProperties,

  featureTitleEnhanced: (
    THEME: Theme,
    active: boolean
  ): React.CSSProperties => ({
    fontSize: 18,
    margin: "6px 0 0 0",
    color: active ? THEME.text : THEME.text,
    textShadow: active ? "0 1px 0 rgba(18,115,81,0.15)" : "none",
  }),

  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "10px 0 0 0",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  featureItemEnhanced: (
    THEME: Theme,
    active: boolean
  ): React.CSSProperties => ({
    color: THEME.subtext,
    fontSize: 14.5,
  }),

  // Founders
  foundersCard: (THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    background: THEME.card,
    border: `1px solid ${THEME.border} `,
    borderRadius: 16,
    padding: isMobile ? 16 : 22,
    boxShadow: THEME.shadow,
  }),
  foundersBadge: (THEME: Theme): React.CSSProperties => ({
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#eef8f3",
    color: THEME.accentSolid,
    fontSize: 12,
    border: `1px solid ${THEME.border} `,
    marginBottom: 10,
  }),
  copy: (THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    color: THEME.subtext,
    fontSize: isMobile ? 15.5 : 16,
    margin: "8px 0",
  }),
  signatureRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 14,
  },
  sigLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  sigLine: (THEME: Theme): React.CSSProperties => ({
    width: 100,
    height: 2,
    background: THEME.accentGradient,
    borderRadius: 2,
  }),
  sigNames: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    fontStyle: "italic",
  }),

  // Pricing
  pricingWrap: (isMobile: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 420px)",
    justifyContent: "center",
  }),
  pricingCard: (THEME: Theme): React.CSSProperties => ({
    background: THEME.card,
    border: `1px solid ${THEME.border} `,
    borderRadius: 16,
    padding: 20,
    boxShadow: THEME.shadow,
  }),
  priceTop: (_THEME: Theme): React.CSSProperties => ({
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginBottom: 12,
  }),
  priceValue: (THEME: Theme): React.CSSProperties => ({
    fontSize: 44,
    fontWeight: 800,
    background: THEME.accentGradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    lineHeight: 1,
  }),
  priceSub: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    marginBottom: 6,
  }),
  priceList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 16px 0",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  priceItem: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    fontSize: 15.5,
  }),

  // ---------- NEW: Advantage section (example layout) ----------
  advWrap: (isMobile: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
    gap: 20,
    alignItems: "center",
  }),
  advImageCol: (isMobile: boolean): React.CSSProperties => ({
    order: isMobile ? 2 : 1, // image bottom on mobile
  }),
  advImage: {
    width: "108%", // slightly wider than container
    height: "auto", // maintain aspect ratio
    maxHeight: 560, // increase height cap
    objectFit: "cover",
    borderRadius: 18,
    boxShadow: "0 24px 48px rgba(0,0,0,0.22)",
    transform: "translateX(-4%)", // optional, re-centers the overhang
  },
  advCopyCol: (THEME: Theme, isMobile: boolean): React.CSSProperties => ({
    order: isMobile ? 1 : 2,
    padding: isMobile ? "0 2px" : "0 48px",
  }),
  advStats: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 18,
    marginTop: 14,
  },
  statBlock: (THEME: Theme): React.CSSProperties => ({
    borderRadius: 14,
    padding: "14px 0",
  }),
  statHeadline: (THEME: Theme): React.CSSProperties => ({
    fontWeight: 900,
    fontSize: 22,
    color: THEME.text,
    letterSpacing: -0.2,
  }),
  statSub: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    marginTop: 6,
    fontSize: 16,
  }),

  // (legacy card styles kept, though not used in new layout)
  advGrid: (isMobile: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
    gap: 14,
  }),
  advCard: (THEME: Theme): React.CSSProperties => ({
    background: THEME.card,
    border: `1px solid ${THEME.border} `,
    borderRadius: 14,
    padding: 16,
    boxShadow: THEME.shadow,
    minHeight: 140,
  }),
  advDot: (THEME: Theme): React.CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: THEME.accentGradient,
    marginBottom: 10,
  }),
  advTitle: (_THEME: Theme): React.CSSProperties => ({
    margin: "0 0 6px 0",
    fontSize: 18,
  }),
  advDesc: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    margin: 0,
  }),

  // FAQ
  faqWrap: {
    maxWidth: 860,
    margin: "0 auto",
  },
  detail: (THEME: Theme): React.CSSProperties => ({
    background: `linear - gradient(180deg, ${THEME.card}, ${THEME.card})`,
    border: `1px solid ${THEME.border} `,
    borderRadius: 12,
    padding: "10px 12px",
    marginBottom: 10,
  }),
  summary: (THEME: Theme): React.CSSProperties => ({
    cursor: "pointer",
    fontWeight: 700,
    color: THEME.text,
    outline: "none",
  }),
  faqAnswer: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    margin: "8px 0 0 0",
    paddingRight: 4,
  }),

  footer: (_THEME: Theme): React.CSSProperties => ({
    background: "#f4f8f6",
    borderTop: `1px solid #e6eeeb`,
  }),
  footerInner: (isMobile: boolean): React.CSSProperties => ({
    maxWidth: 1080,
    margin: "0 auto",
    padding: "28px 16px",
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
    gap: 20,
  }),
  footerBrand: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  footerLogo: (THEME: Theme): React.CSSProperties => ({
    fontWeight: 900,
    letterSpacing: 1,
    fontSize: 18,
    display: "inline-block",
    background: THEME.accentGradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  }),
  footerText: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    margin: 0,
  }),
  footerColsWrap: (isMobile: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
    gap: 14,
  }),
  footerCol: {
    minWidth: 160,
  },
  footerColTitle: (_THEME: Theme): React.CSSProperties => ({
    fontWeight: 700,
    marginBottom: 8,
  }),
  footerLink: (THEME: Theme): React.CSSProperties => ({
    color: THEME.subtext,
    textDecoration: "none",
  }),
  footerBottom: (THEME: Theme): React.CSSProperties => ({
    borderTop: `1px solid ${THEME.border} `,
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    maxWidth: 1080,
    margin: "0 auto",
    color: THEME.subtext,
    fontSize: 13,
  }),

  badge: (THEME: Theme): React.CSSProperties => ({
    alignSelf: "center",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    border: `1px solid rgba(255, 255, 255, 0.2`,
    marginTop: "10vh",
    color: "lightgray",
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 600,
  }),
  pricingCardsRow: (isMobile: boolean) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
    gap: 20,
    marginTop: 24,
  }),
};

/* --------------------------- Buttons --------------------------- */

function buttonPrimary(THEME: Theme): React.CSSProperties {
  return {
    display: "inline-block",
    background: THEME.accentGradient,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 999,
    textDecoration: "none",
    fontWeight: 700,
    boxShadow: THEME.shadow,
    border: "none",
  };
}

function featureCardStyle(
  THEME: Theme,
  isActive: boolean,
  _isMobile: boolean
): React.CSSProperties {
  return {
    position: "relative",
    background: isActive
      ? `linear - gradient(180deg, rgba(42, 168, 128, 0.10), rgba(18, 115, 81, 0.10))`
      : THEME.card,
    border: `1px solid ${isActive ? "#7fd8bf" : THEME.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: isActive ? "0 20px 36px rgba(17,94,72,0.20)" : THEME.shadow,
    transform: isActive
      ? "translateY(-2px) scale(1.01)"
      : "translateY(0) scale(1.0)",
    transition:
      "transform 250ms ease, box-shadow 250ms ease, border-color 250ms ease, background 250ms ease",
    outline: isActive ? "2px solid rgba(42,168,128,0.35)" : "none",
    cursor: "pointer",
  };
}

// Mobile card: snap-centered
function featureCardMobileStyle(
  THEME: Theme,
  isActive: boolean
): React.CSSProperties {
  return {
    ...featureCardStyle(THEME, isActive, true),
    minWidth: "82%",
    scrollSnapAlign: "center",
    scrollSnapStop: "always",
  };
}
